import React, { useState } from "react";
import Button from "./Button";
import ProgressBar from "./ProgressBar";
import { DateTime } from "luxon";
import { useStateContext } from "../contexts/ContextProvider";
import { apiAddLandlordSignature } from "../services/apiService";
import LeasePDF from "./LeasePDF";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { storage } from "../services/firebaseConfig";
import LoadingSpinner from "./LoadingSpinner";

const initialErrorState = {
  fileError: "",
  signatureName: "",
  signatureTerms: "",
};

const initialSignature = {
  font: "font-signature-1",
  terms: false,
};

const LandlordSignLeaseModal = (props) => {
  const { lease } = props;
  const { leases, setLeases, handleModal } = useStateContext();
  const [editedLease, setEditedLease] = useState(lease);
  const [signature, setSignature] = useState(initialSignature);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(initialErrorState);
  const [page, setPage] = useState(1);

  const pages = 2;

  const nextPage = async () => {
    if (hasErrors()) {
      return;
    } else {
      setPage(page + 1);
    }
  };

  const prevPage = () => {
    setPage(page - 1);
  };

  const handleSignature = (e) => {
    const { name, value } = e.target;
    setSignature((prevState) => ({ ...prevState, [name]: value }));
  };

  const updateSignature = () => {
    setEditedLease((prevState) => ({
      ...prevState,
      landlordFont: signature.font,
      landlordDateSigned: DateTime.now().toISO(),
    }));
    nextPage();
  };

  const handleSignLease = async () => {
    if (hasErrors()) {
      return;
    } else {
      setIsLoading(true);
      const url = await handleUploadLease();
      editedLease.leasePdf = url;
      const response = await apiAddLandlordSignature(
        editedLease.id,
        editedLease
      );
      if (response.hasError) {
        //TODO: add error message
        console.log(response.error.response.statusText);
      } else {
        setLeases(
          leases.toSpliced(
            leases.findIndex((x) => x.id === editedLease.id),
            1,
            response.data
          )
        );
        localStorage.removeItem("inviteId");
        handleModal("landlordSignLease");
      }
      setIsLoading(false);
    }
  };

  const handleUploadLease = async () => {
    let leaseUrl = "";
    const html2pdf = await require("html2pdf.js/dist/html2pdf.min.js");
    const options = {
      margin: 20,
      filename: `Lease Agreement_${lease.leaseStart}.pdf`,
    };

    const leaseHTML = document.getElementById("lease");
    await html2pdf()
      .set(options)
      .from(leaseHTML)
      .outputPdf("blob") // Generate the PDF as a blob
      .then(async (blob) => {
        const metadata = {
          contentType: "application/pdf",
        };

        const fileRef = ref(
          storage,
          `leases/${lease.landlordUuid}/${lease.uuid}.pdf`
        );

        // upload file to cloud
        await uploadBytes(fileRef, blob, metadata).then(async (snapshot) => {
          await getDownloadURL(snapshot.ref).then((url) => {
            leaseUrl = url;
          });
        });
      });
    return leaseUrl;
  };

  const hasErrors = () => {
    if (page === 1 && signature.terms === false) {
      activateError("signatureTerms");
      return true;
    } else {
      return false;
    }
  };

  const activateError = (item) => {
    setError((prevState) => ({
      ...prevState,
      [item]: "animate-shake text-red-600 border-red-600",
    }));
    setTimeout(() => {
      setError((prevState) => ({
        ...prevState,
        [item]: "",
      }));
    }, 500);
    clearTimeout();
  };

  return (
    <div
      className="w-screen h-screen fixed top-0 left-0 right-0 bg-white overflow-y-scroll
     mt-20 pb-12 z-10"
    >
      <ProgressBar pages={pages} currentPage={page} />
      <LoadingSpinner isLoading={isLoading} />
      {page === 1 && (
        <div className="grid justify-items-center mt-5">
          <div className="text-2xl">Adopt Your Signature Below:</div>
          <form>
            <label className="grid justify-items-center text-lg mt-5">
              Select a Style:
              <select
                id="font"
                name="font"
                value={signature.font}
                autoFocus={true}
                className="border-gray-300 border-1 rounded-lg
                 outline-snappily-orange p-2"
                onChange={handleSignature}
              >
                <option value="font-signature-1">Style 1</option>
                <option value="font-signature-2">Style 2</option>
                <option value="font-signature-3">Style 3</option>
                <option value="font-signature-4">Style 4</option>
                <option value="font-signature-5">Style 5</option>
              </select>
            </label>
          </form>
          <div className="h-40 text-2xl font-signature-1 pt-10 capitalize">
            <div className={`text-center ${signature.font}`}>
              {editedLease.landlordFirstName +
                (editedLease.landlordMiddleName
                  ? " " + editedLease.landlordMiddleName + " "
                  : " ") +
                editedLease.landlordLastName}
            </div>

            <div className={`text-center pt-5  ${signature.font}`}>
              {editedLease.landlordFirstName[0] +
                (editedLease.landlordMiddleName
                  ? "." + editedLease.landlordMiddleName[0] + "."
                  : ".") +
                editedLease.landlordLastName[0] +
                "."}
            </div>
          </div>
          <div className="h-40 mt-10">
            <div className="flex justify-center items-start px-8">
              <input
                id="checkbox"
                name="tou"
                type="checkbox"
                checked={signature.terms}
                onChange={() =>
                  setSignature((prevState) => ({
                    ...prevState,
                    terms: !signature.terms,
                  }))
                }
                className="mt-3 mr-3"
              />
              <div
                className={`max-w-[500px] text-justify cursor-pointer ${error.signatureTerms}`}
                onClick={() =>
                  setSignature((prevState) => ({
                    ...prevState,
                    terms: !signature.terms,
                  }))
                }
              >
                By checking this box, I agree to adopt the signature and
                initials above as an electronic representation of my signature
                and initials, for the purpose of signing this legally binding
                lease.
              </div>
            </div>
          </div>
        </div>
      )}
      {page === pages && (
        <div className="grid justify-items-center">
          <div className="h-3/4">
            <LeasePDF lease={editedLease} finalSignature={true} />
          </div>
        </div>
      )}
      <div
        className={`flex justify-center w-full bg-white  ${
          page === pages ? "fixed bottom-0 pt-5 pb-40" : "pb-40"
        }`}
      >
        <Button
          background="bg-slate-400"
          text="text-white"
          opacity="bg-opacity-100"
          title={page === 1 ? "Cancel" : "Back"}
          height="h-10"
          width="w-24"
          borderColor="border-none"
          borderHover="hover:border-snappily-orange"
          customFunc={() => {
            page === 1 ? handleModal("landlordSignLease") : prevPage();
          }}
        />
        <Button
          background="bg-snappily-orange"
          text="text-white"
          opacity="bg-opacity-100"
          title={page === pages ? "Sign" : "Next"}
          height="h-10"
          width="w-24"
          borderColor="border-none"
          borderHover="hover:border-snappily-orange"
          customFunc={() => {
            page === pages ? handleSignLease() : updateSignature();
          }}
        />
      </div>
    </div>
  );
};

export default LandlordSignLeaseModal;
